/**
 * Lawyer Remittance — manual entry (Josh 09/29): "a built in dialogue where i input the
 * information that is given to me via the lawyer — deal name (via deal lookup) and
 * invoices when they are given to me". Lawyer defaults to Madeb.
 *
 * Header (lawyer, date, check/wire #, lawyer's total for tie-out) → Collections grid
 * (deal lookup, gross, fee %, fee $, net) → Invoices grid (deal lookup, amount,
 * description, invoice #) → Post. Every row goes through the existing per-deal
 * writers; a deal with no legal case gets one for this lawyer automatically.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Send, Loader2, CheckCircle, XCircle, History } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/legal/remittance`;
const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const blankCol = () => ({ key: Math.random().toString(36).slice(2), deal: null, gross: '', feePct: '', reference: '', notes: '' });
const blankInv = () => ({ key: Math.random().toString(36).slice(2), deal: null, amount: '', description: '', date: '', invoiceRef: '' });

const RemittanceEntry = ({ getAuthHeaders }) => {
  const headers = useCallback(() => ({ headers: getAuthHeaders() }), [getAuthHeaders]);
  const [lawyers, setLawyers] = useState([]);
  const [lawyerId, setLawyerId] = useState('');
  const [date, setDate] = useState(today());
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [sheetTotal, setSheetTotal] = useState('');
  const [cols, setCols] = useState([blankCol()]);
  const [invs, setInvs] = useState([]);
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    axios.get(`${API}/lawyers`, headers()).then(r => {
      setLawyers(r.data);
      const d = r.data.find(l => l.default) || r.data[0];
      if (d) setLawyerId(d.id);
    }).catch(() => toast.error('Could not load lawyers'));
  }, [headers]);

  const loadHistory = useCallback(() => {
    axios.get(`${API}?limit=20`, headers()).then(r => setHistory(r.data.remittances)).catch(() => {});
  }, [headers]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const lawyer = lawyers.find(l => l.id === lawyerId);
  const defaultPct = lawyer?.fee_pct;

  const rowPct = (c) => (c.feePct !== '' ? num(c.feePct) : (c.deal?.contingency_rate ?? defaultPct ?? 0));
  const rowFee = (c) => Math.round(num(c.gross) * rowPct(c)) / 100;
  const filledCols = cols.filter(c => c.deal && num(c.gross) > 0);
  const filledInvs = invs.filter(i => i.deal && num(i.amount) > 0 && i.description.trim());
  const gross = filledCols.reduce((s, c) => s + num(c.gross), 0);
  const fees = filledCols.reduce((s, c) => s + rowFee(c), 0);
  const net = gross - fees;
  const invTotal = filledInvs.reduce((s, i) => s + num(i.amount), 0);
  const tie = sheetTotal !== '' ? net - num(sheetTotal) : null;

  const setCol = (key, patch) => setCols(cs => cs.map(c => (c.key === key ? { ...c, ...patch } : c)));
  const setInv = (key, patch) => setInvs(is => is.map(i => (i.key === key ? { ...i, ...patch } : i)));

  const post = async () => {
    if (!lawyerId) { toast.error('Pick the lawyer'); return; }
    if (!filledCols.length && !filledInvs.length) { toast.error('Enter at least one collection or invoice'); return; }
    if (tie !== null && Math.abs(tie) > 0.005 && !window.confirm(`Net ${formatCurrency(net)} does not match the lawyer's total ${formatCurrency(num(sheetTotal))} (diff ${formatCurrency(tie)}). Post anyway?`)) return;
    setPosting(true);
    try {
      const body = {
        lawyer_contact_id: lawyerId, date, reference, notes,
        remittance_total: sheetTotal === '' ? null : num(sheetTotal),
        collections: filledCols.map(c => ({ deal_id: c.deal.deal_uuid, gross_collected: num(c.gross), fee_pct: rowPct(c), reference: c.reference, notes: c.notes })),
        invoices: filledInvs.map(i => ({ deal_id: i.deal.deal_uuid, amount: num(i.amount), description: i.description, date: i.date || null, invoice_reference: i.invoiceRef })),
      };
      const r = await axios.post(API, body, headers());
      setResult(r.data.remittance);
      const t = r.data.remittance.totals;
      toast[r.data.success ? 'success' : 'warning'](`Posted ${t.collections_posted} collection(s), ${t.invoices_posted} invoice(s)${t.failed ? `, ${t.failed} failed` : ''}`);
      if (r.data.success) { setCols([blankCol()]); setInvs([]); setReference(''); setNotes(''); setSheetTotal(''); }
      loadHistory();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Post failed'));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-base">Lawyer remittance</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[11px] text-muted-foreground">Lawyer</Label>
              <Select value={lawyerId} onValueChange={setLawyerId}>
                <SelectTrigger className="w-[220px]" data-testid="rem-lawyer"><SelectValue placeholder="Pick lawyer" /></SelectTrigger>
                <SelectContent>{lawyers.map(l => <SelectItem key={l.id} value={l.id}>{l.name}{l.fee_pct != null ? ` · ${l.fee_pct}%` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[11px] text-muted-foreground">Remittance date</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-[150px]" data-testid="rem-date" /></div>
            <div><Label className="text-[11px] text-muted-foreground">Check / wire #</Label><Input value={reference} onChange={e => setReference(e.target.value)} className="w-[160px]" placeholder="optional" /></div>
            <div><Label className="text-[11px] text-muted-foreground">Lawyer's net total (tie-out)</Label><Input type="number" step="0.01" value={sheetTotal} onChange={e => setSheetTotal(e.target.value)} className="w-[150px]" placeholder="from their sheet" /></div>
            <div className="flex-1 min-w-[200px]"><Label className="text-[11px] text-muted-foreground">Note</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="optional" /></div>
          </div>

          {/* collections */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium">Collections</p>
              <Button size="sm" variant="outline" onClick={() => setCols(cs => [...cs, blankCol()])}><Plus className="h-3 w-3 mr-1" />Row</Button>
            </div>
            <Table>
              <TableHeader><TableRow className="text-xs">
                <TableHead className="w-[320px]">Deal</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="w-[90px]">Fee %</TableHead>
                <TableHead className="text-right">Fee</TableHead><TableHead className="text-right">Net</TableHead><TableHead>Ref / note</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {cols.map(c => (
                  <TableRow key={c.key}>
                    <TableCell><DealPicker value={c.deal} onChange={d => setCol(c.key, { deal: d, feePct: c.feePct })} headers={headers} lawyerId={lawyerId} /></TableCell>
                    <TableCell><Input type="number" step="0.01" className="h-8 text-right" value={c.gross} onChange={e => setCol(c.key, { gross: e.target.value })} data-testid="rem-gross" /></TableCell>
                    <TableCell><Input type="number" step="0.01" className="h-8" value={c.feePct} placeholder={String(c.deal?.contingency_rate ?? defaultPct ?? '')} onChange={e => setCol(c.key, { feePct: e.target.value })} /></TableCell>
                    <TableCell className="text-right text-xs text-red-700">{c.gross ? formatCurrency(rowFee(c)) : ''}</TableCell>
                    <TableCell className="text-right text-xs font-medium">{c.gross ? formatCurrency(num(c.gross) - rowFee(c)) : ''}</TableCell>
                    <TableCell><Input className="h-8 text-xs" value={c.reference} onChange={e => setCol(c.key, { reference: e.target.value })} placeholder="optional" /></TableCell>
                    <TableCell><Button size="sm" variant="ghost" className="h-7 px-1" onClick={() => setCols(cs => cs.length > 1 ? cs.filter(x => x.key !== c.key) : [blankCol()])}><Trash2 className="h-3 w-3" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* invoices */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium">Invoices / legal costs <span className="text-xs text-muted-foreground font-normal">(filing fees, service, court costs — charged to the deal, shared with syndicators)</span></p>
              <Button size="sm" variant="outline" onClick={() => setInvs(is => [...is, blankInv()])}><Plus className="h-3 w-3 mr-1" />Invoice</Button>
            </div>
            {invs.length > 0 && (
              <Table>
                <TableHeader><TableRow className="text-xs">
                  <TableHead className="w-[320px]">Deal</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Description</TableHead><TableHead className="w-[140px]">Date</TableHead><TableHead>Invoice #</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {invs.map(i => (
                    <TableRow key={i.key}>
                      <TableCell><DealPicker value={i.deal} onChange={d => setInv(i.key, { deal: d })} headers={headers} lawyerId={lawyerId} /></TableCell>
                      <TableCell><Input type="number" step="0.01" className="h-8 text-right" value={i.amount} onChange={e => setInv(i.key, { amount: e.target.value })} /></TableCell>
                      <TableCell><Input className="h-8 text-xs" value={i.description} onChange={e => setInv(i.key, { description: e.target.value })} placeholder="e.g. Court filing fee" /></TableCell>
                      <TableCell><Input type="date" className="h-8 text-xs" value={i.date} onChange={e => setInv(i.key, { date: e.target.value })} /></TableCell>
                      <TableCell><Input className="h-8 text-xs" value={i.invoiceRef} onChange={e => setInv(i.key, { invoiceRef: e.target.value })} placeholder="optional" /></TableCell>
                      <TableCell><Button size="sm" variant="ghost" className="h-7 px-1" onClick={() => setInvs(is => is.filter(x => x.key !== i.key))}><Trash2 className="h-3 w-3" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* totals + post */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-muted/40 text-sm">
            <div className="flex gap-5">
              <span>Gross <b>{formatCurrency(gross)}</b></span>
              <span>Fees <b className="text-red-700">{formatCurrency(fees)}</b></span>
              <span>Net <b className="text-green-700">{formatCurrency(net)}</b></span>
              {invs.length > 0 && <span>Invoices <b>{formatCurrency(invTotal)}</b></span>}
              {tie !== null && <span className={Math.abs(tie) > 0.005 ? 'text-amber-700' : 'text-green-700'}>{Math.abs(tie) > 0.005 ? `off by ${formatCurrency(tie)} vs lawyer's total` : 'ties out'}</span>}
            </div>
            <Button onClick={post} disabled={posting || (!filledCols.length && !filledInvs.length)} data-testid="rem-post">
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Post {filledCols.length} collection{filledCols.length === 1 ? '' : 's'}{filledInvs.length ? ` + ${filledInvs.length} invoice${filledInvs.length === 1 ? '' : 's'}` : ''}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Each collection posts LAWYER_COLLECTION (gross) + LAWYER_FEE on the deal; invoices post LEGAL_COST. A deal without a legal case gets one for this lawyer at the fee % shown. Duplicate rows (same deal, date, amount, reference) are rejected.</p>
        </CardContent>
      </Card>

      {result && <ResultCard r={result} onClose={() => setResult(null)} />}

      <Card>
        <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" />Posted remittances</CardTitle></CardHeader>
        <CardContent>
          {!history?.length && <p className="text-sm text-muted-foreground">None yet.</p>}
          {history?.length > 0 && (
            <Table>
              <TableHeader><TableRow className="text-xs"><TableHead>Date</TableHead><TableHead>Lawyer</TableHead><TableHead>Ref</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Fees</TableHead><TableHead className="text-right">Net</TableHead><TableHead className="text-right">Invoices</TableHead><TableHead>Rows</TableHead></TableRow></TableHeader>
              <TableBody>
                {history.map(h => (
                  <TableRow key={h.id} className="text-xs">
                    <TableCell>{formatDate(h.date)}</TableCell><TableCell>{h.lawyer_name}</TableCell><TableCell>{h.reference || '—'}</TableCell>
                    <TableCell className="text-right">{formatCurrency(h.totals.gross)}</TableCell><TableCell className="text-right">{formatCurrency(h.totals.fees)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(h.totals.net)}</TableCell><TableCell className="text-right">{formatCurrency(h.totals.invoices)}</TableCell>
                    <TableCell>{h.totals.collections_posted} / {h.totals.invoices_posted}{h.totals.failed ? <span className="text-red-700"> · {h.totals.failed} failed</span> : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── deal lookup ─────────────────────────────────────────────────────────────
const DealPicker = ({ value, onChange, headers, lawyerId }) => {
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const p = new URLSearchParams({ q, limit: 15 });
      if (lawyerId) p.set('lawyer_contact_id', lawyerId);
      axios.get(`${API}/deals?${p}`, headers()).then(r => setOpts(r.data)).catch(() => setOpts([]));
    }, 200);
    return () => clearTimeout(timer.current);
  }, [q, open, headers, lawyerId]);

  if (value) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <button className="font-medium hover:underline" onClick={() => { onChange(null); setQ(''); setOpen(true); }} title="change">#{value.deal_id}</button>
        <span className="truncate max-w-[180px]" title={value.deal_name}>{value.deal_name}</span>
        {!value.has_legal_case && <Badge variant="outline" className="text-[10px]">case will be created</Badge>}
      </div>
    );
  }
  return (
    <div className="relative">
      <Input className="h-8 text-xs" value={q} placeholder="Deal # or name…" onChange={e => setQ(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} data-testid="rem-deal-search" />
      {open && opts.length > 0 && (
        <div className="absolute z-20 mt-1 w-[360px] max-h-64 overflow-y-auto bg-popover border rounded shadow text-xs">
          {opts.map(o => (
            <button key={o.deal_uuid} className="w-full text-left px-2 py-1.5 hover:bg-muted flex justify-between gap-2" onMouseDown={() => { onChange(o); setOpen(false); }}>
              <span><b>#{o.deal_id}</b> {o.deal_name}{o.this_lawyer ? '' : o.has_legal_case ? <span className="text-muted-foreground"> · {o.law_firm}</span> : <span className="text-amber-700"> · no case</span>}</span>
              <span className="text-muted-foreground whitespace-nowrap">{formatCurrency(o.balance_with_fees)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ResultCard = ({ r, onClose }) => (
  <Card className="border-indigo-200">
    <CardHeader className="py-3 flex-row items-center justify-between">
      <CardTitle className="text-base">Posted — {formatDate(r.date)} · {r.lawyer_name} · net {formatCurrency(r.totals.net)}{r.totals.tie_out_diff != null && Math.abs(r.totals.tie_out_diff) > 0.005 ? <span className="text-amber-700 text-xs ml-2">off by {formatCurrency(r.totals.tie_out_diff)}</span> : ''}</CardTitle>
      <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
    </CardHeader>
    <CardContent className="text-xs space-y-1">
      {r.collections.map(c => (
        <div key={`c${c.row}`} className="flex items-center gap-2">
          {c.status === 'success' ? <CheckCircle className="h-3 w-3 text-green-600" /> : <XCircle className="h-3 w-3 text-red-600" />}
          <span>#{c.deal_number || c.deal_id} {c.deal_name || ''} — {formatCurrency(c.gross_collected)}{c.fee != null ? ` (fee ${formatCurrency(c.fee)})` : ''}{c.legal_case_created ? ' · legal case created' : ''}</span>
          {c.error && <span className="text-red-700">{typeof c.error === 'string' ? c.error : JSON.stringify(c.error)}</span>}
        </div>
      ))}
      {r.invoices.map(i => (
        <div key={`i${i.row}`} className="flex items-center gap-2">
          {i.status === 'success' ? <CheckCircle className="h-3 w-3 text-green-600" /> : <XCircle className="h-3 w-3 text-red-600" />}
          <span>Invoice #{i.deal_number || i.deal_id} — {formatCurrency(i.amount)} {i.description}</span>
          {i.error && <span className="text-red-700">{typeof i.error === 'string' ? i.error : JSON.stringify(i.error)}</span>}
        </div>
      ))}
    </CardContent>
  </Card>
);

export default RemittanceEntry;
